<template>
  <div class="journey-route">
    <label class="journey-route__label">
      Travelling
      {{ isTravelingToAirport ? "to" : "from" }}
      the Airport:
    </label>
    <search-select-ajax
      class="journey-route__input journey-route__input--origin"
      :value="selected.origin"
      :clear-search-on-select="true"
      :placeholder="labels.origin.placeholder"
      :label="labels.origin.label"
      :returned-options="locations.origin"
      :error-empty-message="labels.origin.errorEmptyMessage"
      @emitSelection="handleLocationChange('origin', $event)"
      @fetchOptions="handleAutocomplete('origin', ...arguments)"
      :disabled="hasTransferMode == airportGettingFrom ? true : false"
    />
    <div
      class="journey-route__switch"
      :class="{ 'journey-route__switch--swap': isTravelingToAirport }"
      @click="handleOrientationSwitch"
    >
      <img :src="icons.switch" alt="Change orientation of Journey" />
    </div>
    <search-select-ajax
      class="journey-route__input journey-route__input--destination"
      :value="selected.destination"
      :clear-search-on-select="true"
      :placeholder="labels.destination.placeholder"
      :label="labels.destination.label"
      :returned-options="locations.destination"
      :error-empty-message="labels.destination.errorEmptyMessage"
      @emitSelection="handleLocationChange('destination', $event)"
      @fetchOptions="handleAutocomplete('destination', ...arguments)"
      :disabled="hasTransferMode == airportGettingTo ? true : false"
    />
  </div>
</template>

<script>
import axios from "axios";
import SearchSelectAjax from "../Inputs/SearchSelectAJAX.vue";

import { JOURNEY_ORIENTATION_FROM, JOURNEY_ORIENTATION_TO } from "./types.js";
import {
  AIRPORT_GETTING_TO,
  AIRPORT_GETTING_FROM,
  REMOTE_TRANSFER
} from "../SearchWidget/config.js";

export default {
  name: "JourneyRoute",
  components: {
    SearchSelectAjax
  },
  props: {
    value: {
      type: String,
      default: () => {
        return JOURNEY_ORIENTATION_TO;
      },
      validator(value) {
        return [JOURNEY_ORIENTATION_TO, JOURNEY_ORIENTATION_FROM].includes(
          value
        );
      }
    },
    transfersMode: {
      type: String,
      default: null
    },
    tenantId: {
      type: String,
      default: null
    },
    routesPrepopOptions: {
      type: Array,
      default: function() {
        return [];
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        this.localValue = value;
      }
    },
    transfersMode: {
      immediate: true,
      handler(value) {
        this.prePopTransfersMode = value;
        this.controlPrepopProps();
      }
    }
  },
  created() {
    import("./switch.svg").then(data => {
      this.icons.switch = data.default;
    });
  },
  data() {
    return {
      icons: {
        switch: null
      },
      localValue: JOURNEY_ORIENTATION_TO,
      airportGettingTo: AIRPORT_GETTING_TO,
      airportGettingFrom: AIRPORT_GETTING_FROM,
      prePopTransfersMode: null,
      restrictionsConfig: {
        countryCode: null,
        route: null,
        lock: false
      },
      locations: {
        origin: [],
        destination: []
      },
      selected: {
        origin: null,
        destination: null
      }
    };
  },
  mounted() {
    this.controlPrepopProps();
  },
  computed: {
    hasTransferMode() {
      return this.prePopTransfersMode == AIRPORT_GETTING_TO ||
        this.prePopTransfersMode == AIRPORT_GETTING_FROM
        ? this.prePopTransfersMode
        : false;
    },
    isTravelingToAirport() {
      return this.localValue === JOURNEY_ORIENTATION_TO;
    },
    isTravelingFromAirport() {
      return this.localValue === JOURNEY_ORIENTATION_FROM;
    },
    labels() {
      if (this.isTravelingToAirport) {
        return {
          origin: {
            label: "Start from",
            placeholder: "City, hotel or postal code",
            errorEmptyMessage: "Please enter a location to depart from"
          },
          destination: {
            label: "Departure airport",
            placeholder: "Search for an airport",
            errorEmptyMessage: "Please enter a departure airport"
          }
        };
      } else {
        return {
          origin: {
            label: "Arrival airport",
            placeholder: "Search for an airport",
            errorEmptyMessage: "Please enter an arrival airport"
          },
          destination: {
            label: "Going to",
            placeholder: "City, hotel or postal code",
            errorEmptyMessage: "Please enter where you are going to"
          }
        };
      }
    }
  },
  methods: {
    controlPrepopProps() {
      if (this.hasTransferMode) {
        /*
         ** XFER-206 - Acceptance Criteria 2/3
         */
        this.prefetchTransfer();
      } else if (
        this.transfersMode === "airport_remote_transfer" &&
        this.tenantId
      ) {
        /*
         ** XFER-206 - Acceptance Criteria 1
         */
        this.$emit("componentReady");
      }
      this.locations.origin = this.routesPrepopOptions;
    },
    prefetchTransfer() {
      return axios({
        method: "GET",
        url:
          "https://autocomplete." +
          process.env.VUE_APP_ENV +
          ".transfers.maginfrastructure.com/api/v1/TenantLocation",
        headers: {
          "Access-Control-Allow-Origin": "*",
          "X-MAGO-TENANT-ID": this.tenantId
        }
      })
        .then(response => {
          response = response.data;

          if (this.hasTransferMode == AIRPORT_GETTING_TO) {
            this.selected.destination = response;
            /*
             ** Add custom keys for vue-select suppory
             */
            this.updateRestrictions({
              countryCode: response.location.address.countryCode,
              route: "destination",
              lock: true
            });
            this.selected.destination.label = response.name;
            this.selected.destination.value = response.iata;
            this.handleLocationChange("destination", this.selected.destination);
          }
          if (this.hasTransferMode == AIRPORT_GETTING_FROM) {
            this.handleOrientationSwitch();
            this.selected.origin = response;
            /*
             ** Add custom keys for vue-select suppory
             */
            this.updateRestrictions({
              countryCode: response.location.address.countryCode,
              route: "origin",
              lock: true
            });
            this.selected.origin.label = response.name;
            this.selected.origin.value = response.iata;
            this.handleLocationChange("origin", this.selected.origin);
          }
          this.$emit("componentReady");
        })
        .catch(err => {
          window.alert(`Get Tenant Location error:\n${err}`);
        });
    },
    handleOrientationSwitch() {
      const origin = this.selected.origin;
      const destination = this.selected.destination;
      this.selected.origin = destination;
      this.selected.destination = origin;

      const originArr = this.locations.origin;
      const destinationArr = this.locations.destination;
      this.locations.origin = destinationArr;
      this.locations.destination = originArr;

      if (this.localValue === JOURNEY_ORIENTATION_TO) {
        this.localValue = JOURNEY_ORIENTATION_FROM;
        if (this.transfersMode != REMOTE_TRANSFER) {
          this.prePopTransfersMode = AIRPORT_GETTING_FROM;
        }
      } else {
        this.localValue = JOURNEY_ORIENTATION_TO;
        if (this.transfersMode != REMOTE_TRANSFER) {
          this.prePopTransfersMode = AIRPORT_GETTING_TO;
        }
      }
      /*
       ** Update the restrictions based on the rotation
       */
      this.updateRestrictions({
        countryCode: this.restrictionsConfig.countryCode,
        route:
          this.restrictionsConfig.route === "origin" ? "destination" : "origin"
      });
      this.$emit("origin", this.selected.origin);
      this.$emit("destination", this.selected.destination);
      this.$emit("input", this.localValue);
    },
    autocompleteEndpoint(journey) {
      let url =
        process.env.ENDPOINT_AUTOCOMPLETE_URI ||
        "https://autocomplete." +
          process.env.VUE_APP_ENV +
          ".transfers.maginfrastructure.com";
      if (
        (journey === "ORIGIN" && this.isTravelingFromAirport) ||
        (journey === "DESTINATION" && this.isTravelingToAirport)
      ) {
        url = url + "/api/v1/AirportSearch";
      } else {
        url = url + "/api/v1/AutoComplete";
      }

      return url.toString();
    },
    handleLocationChange(routePart, data) {
      this.selected[routePart] = data;
      this.handleCountryCodeChange(
        data.location.address.countryCode,
        routePart
      );
      this.updateRestrictions({
        countryCode: data.location.address.countryCode
      });
      if (!this.restrictionsConfig.route) {
        /*
         ** update the restrictions is the lock controller is not set.
         ** primairly used when widget prepoppulation is active.
         */
        this.updateRestrictions({
          route: routePart
        });
      }
      this.$emit(routePart, data);
    },
    handleCountryCodeChange(newCountryCode, routePart) {
      if (
        this.restrictionsConfig.countryCode &&
        newCountryCode != this.restrictionsConfig.countryCode
      ) {
        /*
         ** if CountryCode changes
         */
        if (routePart === "origin") {
          /*
           ** if change made to origin clear destination values
           */
          this.locations.destination = [];
          this.selected.destination = null;
        } else if (routePart === "destination") {
          /*
           ** else if change made to destination clear origin values
           */
          this.locations.origin = [];
          this.selected.origin = null;
        }
      }
      // else do nothing
    },
    handleAutocomplete(journey, term, loading) {
      let parameters = {
        SearchTerm: term.trim(),
        Location: term.trim(),
        CountryCode:
          this.restrictionsConfig.countryCode &&
          this.restrictionsConfig.route != journey
            ? this.restrictionsConfig.countryCode
            : null
      };
      const airportRoute = this.isTravelingToAirport ? "destination" : "origin";
      if (this.selected[airportRoute] && journey != airportRoute) {
        /*
         ** If an airport is selected, use the coords
         */
        parameters.AirportLatitude = this.selected[
          airportRoute
        ].location.coordinates.latitude;
        parameters.AirportLongitude = this.selected[
          airportRoute
        ].location.coordinates.longitude;
      }

      return axios({
        method: "GET",
        url: this.autocompleteEndpoint(journey.toUpperCase()),
        headers: {
          "Access-Control-Allow-Origin": "*",
          "X-MAGO-TENANT-ID": "eaff4ad7-5fe7-46c6-93f7-16cf1691db1c"
        },
        params: parameters
      })
        .then(res => this.handleAutocompleteResponse(journey, res))
        .then(() => {
          loading(false);
        })
        .catch(err => {
          window.alert(`Autocomplete error:\n${err}`);
          return [];
        });
    },
    handleAutocompleteResponse(journey, { data }) {
      if (Object.prototype.hasOwnProperty.call(data, "airports")) {
        this.locations[journey] = data.airports.map(airport => {
          return Object.assign({}, airport, {
            label: airport.name,
            value: airport.iata
          });
        });
      } else {
        this.locations[journey] = data.map(location => {
          return Object.assign({}, location, {
            label: location.description,
            value: location.placeId
          });
        });
      }
      return this.locations;
    },
    updateRestrictions(payload) {
      payload.countryCode
        ? (this.restrictionsConfig.countryCode = payload.countryCode)
        : null;
      payload.route ? (this.restrictionsConfig.route = payload.route) : null;
      /*
       ** Lock is used to allow/disallow changing restructions on the original countryCode controller.
       */
      payload.lock ? (this.restrictionsConfig.lock = payload.lock) : null;
    }
  }
};
</script>

<style lang="scss" scoped>
.journey-route {
  position: relative;
}
.journey-route__label {
  position: relative;
  display: block;
  margin-bottom: 15px;
  padding: 4px;
  font-weight: bold;
  font-size: 15px;
  color: $primary;
}
.journey-route__switch {
  position: absolute;
  top: calc(50% - 3px);
  right: 16px;
  width: 48px;
  height: 48px;
  border: 1px solid #d3dce6;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  z-index: 2;
  cursor: pointer;
  transition-property: border-color, transform;
  transition-duration: 150ms;
  transition-timing-function: ease-in-out;
}

.journey-route__switch:hover {
  border: 1px solid #196ed5;
}

.journey-route__switch--swap {
  transform: rotate(180deg);
}

.journey-route__input--origin {
  margin-bottom: 16px;
}

.journey-route__input {
  position: relative;
}

.journey-route__input >>> .validation-message {
  position: absolute;
  pointer-events: none;
  font-size: 10px;
  bottom: -7px;
  left: 9px;
  z-index: 2;
  padding: 8px 4px;
  width: calc(100% - 8px);
}
</style>
